import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient, setAccessToken, clearAccessToken } from './client';
import { AxiosResponse } from 'axios';

// Types
export interface User {
  uuid: string;
  name: string;
  email: string;
}

export interface RegisterUserRequest {
  name: string;
  email: string;
}

export interface VerifyUserEmailRequest {
  email: string;
}

export interface VerifyUserCodeRequest {
  email: string;
  code: string;
}

export interface VerifyUserEmailResponse {
  is_verified: boolean;
  email: string;
}

export interface LoginUserRequest {
  email: string;
  password: string;
}

export interface LoginUserResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface AccessTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface ChangePasswordRequest {
  password: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  email: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface ResetPasswordConfirmRequest {
  email: string;
  code: string;
}

export interface SocialLoginRequest {
  provider: string;
  id_token: string;
  access_token: string;
}

// POST /api/register - Register a new user
export const useRegisterUser = () => {
  return useMutation({
    mutationFn: async (userData: RegisterUserRequest): Promise<AccessTokenResponse> => {
      try {
        return await apiClient.post<AccessTokenResponse>('/auth/register', userData);
      } catch (error: any) {
        console.error('Failed to register user:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      // Store the access token from registration
      setAccessToken(data.access_token);
    },
    onError: (error) => {
      console.error('Failed to register user:', error);
    },
  });
};


export const useVerifyUserEmail = () => {
  return useMutation({
    mutationFn: async (userData: VerifyUserEmailRequest): Promise<VerifyUserEmailResponse> => {
      try {
        return await apiClient.post<VerifyUserEmailResponse>('/auth/verify-email', userData);
      } catch (error: any) {
        console.error('Failed to verify user email:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      return data;
    },
    onError: (error) => {
      console.error('Failed to login user:', error);
    },
  });
};

export const useVerifyUserCode = () => {
  return useMutation({
    mutationFn: async (userData: VerifyUserCodeRequest): Promise<AccessTokenResponse> => {
      try {
        return await apiClient.post<AccessTokenResponse>('/auth/verify-code', userData);
      } catch (error: any) {
        console.error('Failed to verify user email:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      return data;
    },
    onError: (error) => {
      console.error('Failed to login user:', error);
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (userData: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
      try {
        const response: AxiosResponse<ChangePasswordResponse> = await apiClient.post('/api/users/me/password', userData);
        return response.data;
      } catch (error: any) {
        console.error('Failed to change user password:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      return data;
    },
    onError: (error) => {
      console.error('Failed to change user password:', error);
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (userData: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
      try {
        return await apiClient.post<ResetPasswordResponse>('/auth/reset-password', userData);
      } catch (error: any) {
        console.error('Failed to reset password:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      return data;
    },
    onError: (error) => {
      console.error('Failed to reset password:', error);
    },
  });
};

export const useResetPasswordConfirm = () => {
  return useMutation({
    mutationFn: async (userData: ResetPasswordConfirmRequest): Promise<AccessTokenResponse> => {
      try {
        return await apiClient.post<AccessTokenResponse>('/auth/reset-password-confirm', userData);
      } catch (error: any) {
        console.error('Failed to confirm reset password:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      return data;
    },
    onError: (error) => {
      console.error('Failed to confirm reset password:', error);
    },
  });
};


// POST /auth/login - Login user
export const useLoginUser = () => {
  return useMutation({
    mutationFn: async (userData: LoginUserRequest): Promise<LoginUserResponse> => {
      try {
        return await apiClient.post<LoginUserResponse>('/auth/login', userData);
      } catch (error: any) {
        console.error('Failed to login user:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      setAccessToken(data.access_token);
    },
    onError: (error) => {
      console.error('Failed to login user:', error);
    },
  });
};

// POST /auth/refresh - Get new access token using refresh token cookie
export const useRefreshToken = () => {
  return useMutation({
    mutationFn: async (): Promise<AccessTokenResponse> => {
      try {
        return await apiClient.post<AccessTokenResponse>('/auth/refresh');
      } catch (error: any) {
        console.error('Failed to refresh token:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      // Store the new access token
      setAccessToken(data.access_token);
    },
    onError: () => {
      // Clear access token on refresh failure
      clearAccessToken();
    },
  });
};

// GET /auth/me - Get current user info (requires access token)
export const useRefreshUser = () => {
  return useQuery({
    queryKey: ['user', 'me'],
    queryFn: async (): Promise<User> => {
      try {
        const response = await apiClient.get<User>('/api/users/me');
        return response;
      } catch (error: any) {
        // If 403, user is not logged in - this is normal, don't log as error
        if (error?.response?.status === 403) {
          throw error; // Still throw to indicate no user data, but don't log
        }
        console.error('Failed to get user:', error?.message);
        throw error;
      }
    },
    retry: false, // Don't retry on auth failures
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

export const useLogout = () => {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      return await apiClient.post('/auth/logout');
    },
    onSuccess: () => {
      clearAccessToken();
    },
    onError: (error) => {
      console.error('Failed to logout:', error);
      clearAccessToken();
    },
  });
};

export const useGoogleLogin = () => {
  return useMutation({
    mutationFn: async (googleData: SocialLoginRequest): Promise<AccessTokenResponse> => {
      try {
        return await apiClient.post<AccessTokenResponse>('/auth/social', googleData);
      } catch (error: any) {
        console.error('Failed to login with Google:', error?.message);
        throw error;
      }
    },
    onSuccess: (data) => {
      setAccessToken(data.access_token);
    },
    onError: (error) => {
      console.error('Failed to login with Google:', error);
    },
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: async (userUpdates: Partial<User>): Promise<User> => {
      const response: AxiosResponse<User> = await apiClient.put('/api/users/me', userUpdates);
      return response.data;
    },
  });
};

// Delete Account
export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.delete('/api/users/me');
    },
  });
};

export { getAccessToken } from './client';
